import { StateDeclaration } from '@uirouter/angularjs'
import { ModalFactory } from '@vend/vend.ui'
import some from 'lodash/some'
import startsWith from 'lodash/startsWith'

import { STATES } from '../../../../lib/routes'
import { $log, $q, $state } from '../../ng-compat/imports'
import { OfflineModalController as controller } from './offline-modal/offline-modal.controller'
import template from './offline-modal/offline-modal.html'

/**
 * @class RouteManagerService
 */
export class RouteManagerService {
  public OFFLINE_MODE_STATES: $TSFixMe
  public TRAINING_MODE_STATES: $TSFixMe
  public registerOfflineModal: $TSFixMe

  constructor(private Modal: ModalFactory, private ModalService: $TSFixMe) {
    'ngInject'
    this.TRAINING_MODE_STATES = [STATES.CURRENT_SALE]
    this.OFFLINE_MODE_STATES = [STATES.CURRENT_SALE, STATES.STATUS]
  }

  /**
   * Determine whether the given (or current) state is allowed to be accessed in training mode. If no state is
   * provided the current state will be evaluated instead.
   *
   * @method isStateAllowedInTrainingMode
   *
   * @param {$state|String} state
   *        The state config or name of the state to determine access for
   *
   * @return {Boolean} true if the given state can be accessed in training mode, false otherwise
   */
  public isStateAllowedInTrainingMode(state: StateDeclaration | string) {
    return this._isStateListed(state, this.TRAINING_MODE_STATES)
  }

  /**
   * Determine whether the given (or current) state is allowed to be accessed in offline mode. If no state is
   * provided the current state will be evaluated instead.
   *
   * @method isStateAllowedInOfflineMode
   *
   * @param {$state|String} state
   *        The state config or name of the state to determine access for
   *
   * @return {Boolean} true if the given state can be accessed in offline mode, false otherwise
   */
  public isStateAllowedInOfflineMode(state?: StateDeclaration | string) {
    return this._isStateListed(state, this.OFFLINE_MODE_STATES)
  }

  /**
   * Open the offline modal after ensuring that all other modals have been closed.
   *
   * @method openOfflineModal
   *
   * @return {Promise} Resolved once the modal have been closed
   */
  public openOfflineModal() {
    return this._closeAllOpenModals().then(() =>
      this.Modal({
        template,
        controller,
        controllerAs: '$ctrl',
      })
        .then(modal => {
          this.registerOfflineModal = modal
          return modal.closed
        })
        .catch(() =>
          $log.debug('[RouteManagerService] Failed to open offline modal.')
        )
        .finally(() => {
          this.registerOfflineModal = null
        })
    )
  }

  /**
   * Closes the offline modal.
   *
   * @method closeOfflineModal
   */
  public closeOfflineModal() {
    if (this.registerOfflineModal) {
      this.registerOfflineModal.closeModal()
    }
  }

  /**
   * Responsible for closing all other opened modals.
   *
   * @private
   *
   * @method _closeAllOpenModals
   *
   * @return {Promise} Resolved once all other modals have been closed.
   */
  private _closeAllOpenModals() {
    let closeAllModalsResolver = $q.resolve()
    const openModals = this.ModalService.openModals
    if (openModals && openModals.length) {
      openModals.forEach((modalInstance: $TSFixMe) => {
        modalInstance.close()
        closeAllModalsResolver = closeAllModalsResolver.finally(
          () => modalInstance.modal.closed
        )
      })
    }

    return closeAllModalsResolver
  }

  /**
   * Determine if a given state is listed within the collection of states.
   *
   * @private
   *
   * @method _isStateListed
   *
   * @param {$state|String} [state]
   *        The state to assess.
   *
   * @param {Array} states
   *        The collection of states to check.
   *
   * @return {Boolean} True if the state is present within the collection, false otherwise.
   */
  private _isStateListed(
    state: StateDeclaration | string = $state.current,
    states: $TSFixMe[]
  ) {
    const stateName = (state as $TSFixMe).name || state
    return some(states, allowedState => startsWith(stateName, allowedState))
  }
}
